<script>
import { mapActions, mapGetters } from 'vuex'
import baseMixin from '@/mixins/baseMixin'
import { DisputePerspective } from '@/constants/disputes/DisputePerspective'
import SideDrawer from '@/components/general/side-drawer-dialog.vue'
import Spinner from '@/components/general/spinner.vue'
import DisputesTable from './disputes-table.vue'
import DisputesDialogContent from './disputes-dialog/disputes-dialog-content.vue'
import InvoiceCenterDialogContent from './disputes-dialog/invoice-bulk-action-dialog-content.vue'
import DisputesTableOverviewStatsBreakdownCard from './disputes-table-overview-stats-breakdown-card.vue'
import DisputeTableInvoiceCenterStatsBreakdownCard from './dispute-table-invoice-center-stats-breakdown-card.vue'
import DisputeResolutionConfirmationDialog from './dispute-resolution-confirmation-dialog.vue'

export default {
  name: 'BookingDisputeList',
  components: {
    SideDrawer,
    DisputesDialogContent,
    InvoiceCenterDialogContent,
    Spinner,
    DisputesTable,
    DisputesTableOverviewStatsBreakdownCard,
    DisputeTableInvoiceCenterStatsBreakdownCard,
    DisputeResolutionConfirmationDialog,
  },
  mixins: [baseMixin],
  data() {
    return {
      timesheetSelectTypes: ['All', 'Missing', 'Conflict'],
      adminIds: [],
      disputePerspective: DisputePerspective,
      selectedTimesheets: [],
      isFlyoutLoading: false,
      isGraphLoading: false,
      invoiceCenterId: '',
      invoiceCenterDateUntil: null,
      graphLabels: ['Pending', 'Candidate', 'Client'],
      graphData: [],
      tableOverviewDataPoints: [],
    }
  },
  computed: {
    ...mapGetters({
      isLoadingBookingDisputes: 'disputes/isLoading',
      allowedToViewAdminIds: 'disputes/getAllowedToViewAdminIds',
      currentUser: 'auth/currentUser',
      activeTimesheet: 'disputes/activeTimesheet',
      resolveStatus: 'disputes/resolveStatus',
      showFlyout: 'disputes/showFlyout',
      showResolveContent: 'disputes/showResolveContent',
      showInvoiceCenterContent: 'disputes/showInvoiceCenterContent',
      getInvoiceCenterGroupedData: 'disputes/getInvoiceCenterGroupedData',
      isFinanceUser: 'auth/isFinanceUser',
      invoiceCenter: 'disputes/invoiceCenter',
      isLoadingDisputeSubmit: 'disputes/isLoadingDisputeSubmit',
    }),
    autocompleteSelectedModel: {
      get() {
        return this.adminIds
      },
      set(val) {
        this.adminIds = val
      },
    },
    invoiceCenterData() {
      return this.invoiceCenter(this.invoiceCenterId)
    },
    isLoading() {
      return this.isFlyoutLoading || this.isLoadingDisputeSubmit
    },
  },
  methods: {
    ...mapActions({
      getBookingDisputeList: 'disputes/getDisputes',
      submitTimesheets: 'disputes/submitTimesheets',
      getDisputeDetailsById: 'disputes/getDisputeDetailsById',
    }),
    updateSelectedAdmins(selectedAdminIds) {
      this.autocompleteSelectedModel = selectedAdminIds
    },
    setGraphData(data) {
      this.graphData = data
    },
    async loadList(refresh) {
      await this.$refs.disputesTable.loadList(refresh)
    },
    async loadBookingDetails(item) {
      this.isFlyoutLoading = true
      this.$store.commit('disputes/SET_FLYOUT', { key: 'show', value: true })
      const { data } = await this.getDisputeDetailsById(item.bookingId)
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'timesheet',
        value: data,
      })
      this.isFlyoutLoading = false
    },
    openInvoiceCenterFlyout({ invoiceCenterId, date }) {
      this.isFlyoutLoading = true
      this.invoiceCenterId = invoiceCenterId
      this.invoiceCenterDateUntil = date

      this.$store.commit('disputes/SET_FLYOUT', { key: 'show', value: true })

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'invoiceCenter',
        value: this.invoiceCenterData,
      })

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'invoiceCenterDateUntil',
        value: this.invoiceCenterDateUntil,
      })
      this.isFlyoutLoading = false
    },
    closeFlyout() {
      this.$store.commit('disputes/RESET_FLYOUT')
    },
    async submitInvoiceCenterBulkAction() {
      this.isFlyoutLoading = true
      await this.bulkResolveByInvoiceCenter()
      this.isFlyoutLoading = false
      this.closeFlyout()
      await this.loadList(true)
    },
  },
}
</script>

<template>
  <div class="mt-4">
    <v-row fill-height>
      <v-col cols="12" md="6" fill-height>
        <DisputesTableOverviewStatsBreakdownCard
          :disputes="graphData"
          @adminsUpdated="updateSelectedAdmins"
        />
      </v-col>
      <v-col v-if="isFinanceUser" cols="12" md="6">
        <DisputeTableInvoiceCenterStatsBreakdownCard
          @openInvoiceCenterFlyout="openInvoiceCenterFlyout"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <DisputesTable
          ref="disputesTable"
          :autocomplete-selected-model="autocompleteSelectedModel"
          @loadBookingDetails="loadBookingDetails"
          @setGraphData="setGraphData"
        />
      </v-col>
    </v-row>
    <SideDrawer
      :show="showFlyout"
      :xl="5"
      :lg="6"
      :md="10"
      :title="$t('dispute.resolutionDialog.title')"
      @closeDialog="closeFlyout"
    >
      <DisputesDialogContent v-if="showResolveContent && !isLoading" />
      <InvoiceCenterDialogContent
        v-if="showInvoiceCenterContent && !isLoading"
      />
      <Spinner v-if="isLoading" class="mt-10" size="60" />
      <template v-slot:actions="{ close }">
        <v-spacer />
        <v-btn
          data-testid="side-drawer-dialog-close-btn"
          :large="!isMediumOrLower"
          depressed
          :disabled="isLoading"
          :color="`grey ${isDarkMode ? 'darken-5' : 'lighten-4'}`"
          class="px-8"
          @click="close"
        >
          {{ $t('general.cancel') }}
        </v-btn>
      </template>
    </SideDrawer>
    <DisputeResolutionConfirmationDialog @complete="loadList(true)" />
  </div>
</template>
